import { DistanceUnit } from '~/constants/distance';
import * as i18n from 'i18next';
import { DryIceFormValues } from '~/pages/Orders/All/MultipleOrdersActions/AdditionalServicesForm';
import { isNil } from 'ramda';
import { isParcelTemplate } from '~/types/ParcelTemplate';
import { isDecimal, isNDecimalPlaces, isNonZero } from '~/utils/validations';
import { PackageTemplateChooserFormValues } from './types';

type DryIceFormErrors = Partial<Record<keyof DryIceFormValues, string>>;

export interface PackageTemplateChooserErrors {
  // TODO: Type these.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  weight: Maybe<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  weightUnit: Maybe<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  parcelTemplate: Maybe<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  customDimensions: Maybe<any>;
}

export interface ExtrasErrors {
  dryIce: Maybe<DryIceFormErrors>;
}

type DryIceErrors = {
  WeightErr: string;
  PackageInfoMissingErr: string;
  GreaterThanPackageWeightErr: string;
};

// mapping for dry ice errors - that needs a handle on the translater
const dryIceErrors = (t: (s: string) => string): DryIceErrors => {
  const path = 'orders:additionalServices:errs:dryIce';
  return {
    WeightErr: t(`${path}:weightGtZero`),
    PackageInfoMissingErr: t(`${path}:missingPackageInfo`),
    GreaterThanPackageWeightErr: t(`${path}:dryIceGtPackageWeight`),
  };
};

const validateDryIce = (values: PackageTemplateChooserFormValues): Maybe<DryIceFormErrors> => {
  const dryIceErrTranslations: DryIceErrors = dryIceErrors(i18n.t);
  const { weight, dryIce: dryIceForm } = values;

  const packageWeight = weight || dryIceForm.packageWeight;

  if (dryIceForm.containsDryIce) {
    // make sure the weight is a valid number > 0
    if (
      !dryIceForm.weight ||
      !isDecimal(dryIceForm.weight) ||
      !isNonZero(dryIceForm.weight) ||
      parseFloat(dryIceForm.weight) <= 0
    ) {
      return { weight: dryIceErrTranslations.WeightErr };
    }

    // if we are in batch mode with multiple orders
    // then skip further validation
    if (dryIceForm.inBatchMode) {
      return null;
    }

    // package weight must exist prior to checking dry ice
    // weight against it
    if (isNil(packageWeight)) {
      return {
        weight: dryIceErrTranslations.PackageInfoMissingErr,
      };
    }

    if (Number.parseFloat(dryIceForm.weight) > Number.parseFloat(packageWeight)) {
      return {
        weight: dryIceErrTranslations.GreaterThanPackageWeightErr,
      };
    }
  }

  return null;
};

export const validateExtras = (values: PackageTemplateChooserFormValues) => {
  const errors: ExtrasErrors = {
    dryIce: null,
  };

  if (!values.dryIce) {
    return errors;
  }

  errors.dryIce = validateDryIce(values);

  return errors;
};

const validate = (values: PackageTemplateChooserFormValues) => {
  const errors: PackageTemplateChooserErrors = {
    weight: null,
    weightUnit: null,
    parcelTemplate: null,
    customDimensions: {
      width: null,
      length: null,
      height: null,
    },
  };

  if (!values.parcelTemplate && values.extras && !values.extras.isUsingCustomDimensions) {
    errors.parcelTemplate = 'Please select a parcel template or enter custom dimensions.';
  }

  if (
    values.extras &&
    values.extras.isWeightRequired &&
    !values.weight &&
    !values.useImportedOrderWeight
  ) {
    errors.weight = 'Please enter the package weight';
  }

  if (values.weight && !isNDecimalPlaces(values.weight, 4)) {
    errors.weight = 'Weight must not be more than 4 decimal places (ex. 0.0001)';
  }

  if (values.weight && (!isDecimal(values.weight) || !isNonZero(values.weight))) {
    errors.weight = 'Weight must be greater than 0';
  }

  if (values.weight && !values.weightUnit) {
    errors.weightUnit = 'Please select a weight unit';
  }

  if (values.extras && values.extras.isUsingCustomDimensions && values.customDimensions) {
    if (!values.customDimensions.width) {
      errors.customDimensions.width = 'Width is required';
    } else if (!isDecimal(values.customDimensions.width) || +values.customDimensions.width <= 0) {
      errors.customDimensions.width = 'Width must be greater than 0';
    }

    if (!values.customDimensions.length) {
      errors.customDimensions.length = 'Length is required';
    } else if (!isDecimal(values.customDimensions.length) || +values.customDimensions.length <= 0) {
      errors.customDimensions.length = 'Length must be greater than 0';
    }

    if (!values.customDimensions.height) {
      errors.customDimensions.height = 'Height is required';
    } else if (!isDecimal(values.customDimensions.height) || +values.customDimensions.height <= 0) {
      errors.customDimensions.height = 'Height must be greater than 0';
    }

    if (!values.customDimensions.distanceUnit) {
      errors.customDimensions.distanceUnit = 'Please select a distance unit';
    }
  }

  if (
    values.extras &&
    values.customDimensions &&
    values.extras.carrierTemplateWithCustomDimensions
  ) {
    if (!values.customDimensions.width) {
      errors.customDimensions.width = 'Width is required';
    } else if (!isDecimal(values.customDimensions.width) || +values.customDimensions.width <= 0) {
      errors.customDimensions.width = 'Width must be greater than 0';
    }

    if (!values.customDimensions.length) {
      errors.customDimensions.length = 'Length is required';
    } else if (!isDecimal(values.customDimensions.length) || +values.customDimensions.length <= 0) {
      errors.customDimensions.length = 'Length must be greater than 0';
    }

    if (
      values.parcelTemplate &&
      (values.parcelTemplate.name === 'Soft Pack' ||
        (isParcelTemplate(values.parcelTemplate) &&
          values.parcelTemplate.carrierTemplate &&
          values.parcelTemplate.carrierTemplate.name === 'Soft Pack'))
    ) {
      const sumLengthWidth = +values.customDimensions.length + +values.customDimensions.width;
      if (values.customDimensions.distanceUnit.value.toString() === DistanceUnit.IN) {
        if (+values.customDimensions.length > 18.0) {
          errors.customDimensions.length =
            'For USPS Soft Pack, length cannot be greater than 18 inches';
        } else if (+values.customDimensions.width > 18.0) {
          errors.customDimensions.width =
            'For USPS Soft Pack, width cannot be greater than 18 inches';
        }
        if (sumLengthWidth > 36.0) {
          errors.customDimensions.length = errors.customDimensions.width =
            'The combined length and width cannot exceed 36 inches. For Soft Packs that exceed 36 inches, please use Custom Dimensions.';
        }
      } else if (values.customDimensions.distanceUnit.value.toString() === DistanceUnit.CM) {
        if (+values.customDimensions.length > 45.72) {
          errors.customDimensions.length =
            'For USPS Soft Pack, length cannot be greater than 45.72 cm';
        } else if (+values.customDimensions.width > 45.72) {
          errors.customDimensions.width =
            'For USPS Soft Pack, width cannot be greater than 45.72 cm';
        }
        if (sumLengthWidth > 91.44) {
          errors.customDimensions.length = errors.customDimensions.width =
            'The combined length and width cannot exceed 91.44 cm. For Soft Packs that exceed 91.44 cm, please use Custom Dimensions.';
        }
      }
    }
  }

  return errors;
};

export default validate;
